import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../../components/ui/ModalHeader';
import { isValidEmail } from '../../utils/email';
import {
  bulkEmail, bulkSize, documentsSearchErrorMessage,
} from '../../services/documentsSearch.service';
import { MAX_BULK_SELECTION } from '../../types/documentsSearch';
import type {
  BulkEmailResponse, BulkItem, BulkSizeResponse,
} from '../../types/documentsSearch';

/**
 * Emails the Documents-view selection as attachments.
 *
 * Every cap here mirrors backend/app/schemas/documents_search.py — the file
 * count (MAX_BULK_SELECTION / MAX_BULK_FILES), the recipient count and the
 * note length. Exceeding any of them server-side produces a raw pydantic
 * validation message, so the UI has to hold the line first.
 *
 * The SIZE cap is deliberately not a constant: `bulkSize` reports the server's
 * own `limit_bytes` (MAX_SENDGRID_SIZE) and the modal renders whatever it is
 * told, so raising the backend limit never leaves a stale number on screen.
 */
export interface BulkEmailModalProps {
  /** The selection to attach. Must be a STABLE array — it keys the size fetch. */
  items: BulkItem[];
  /** Pre-filled first recipient (the signed-in user); '' for none. */
  defaultRecipient: string;
  onClose: () => void;
  /** Fired after a successful POST, whether or not some documents failed. */
  onSent?: (res: BulkEmailResponse) => void;
}

/** Mirrors BulkEmailRequest.recipients max_length. */
const MAX_RECIPIENTS = 20;
/** Mirrors BulkEmailRequest.note max_length. */
const MAX_NOTE = 2000;

const BYTES_PER_MB = 1024 * 1024;

/** "12.4 MB" / "20 MB" — one decimal, dropped when it is a zero. */
function formatMb(bytes: number): string {
  const s = (bytes / BYTES_PER_MB).toFixed(1);
  return `${s.endsWith('.0') ? s.slice(0, -2) : s} MB`;
}

const FIELD =
  'w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 '
  + 'placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 '
  + 'dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100';

export const BulkEmailModal: React.FC<BulkEmailModalProps> = ({
  items, defaultRecipient, onClose, onSent,
}) => {
  // The selection can only exceed the cap through a bug upstream (the action
  // bar disables Email above it), but a modal that silently 422s would be
  // worse than one that says why.
  const overCap = items.length > MAX_BULK_SELECTION;

  const [recipients, setRecipients] = useState<string[]>(
    () => (isValidEmail(defaultRecipient) ? [defaultRecipient.trim()] : []),
  );
  const [draft, setDraft] = useState('');
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const [size, setSize] = useState<BulkSizeResponse | null>(null);
  const [sizing, setSizing] = useState(() => !overCap && items.length > 0);
  const [sizeError, setSizeError] = useState<string | null>(null);

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkEmailResponse | null>(null);

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  // Capture-phase + stopPropagation so the host drawer's own window-level
  // Escape handler doesn't close everything behind this modal.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  // Keyed on the documents themselves, not the array identity: a caller that
  // rebuilds `items` every render would otherwise re-request the size forever.
  // The full composite is needed — `ref` alone repeats across sources.
  const itemsKey = useMemo(
    () => items.map((i) => `${i.fileinfo_recno}_${i.ref}_${i.source}_${i.pronumber}`).join(','),
    [items],
  );
  useEffect(() => {
    if (overCap || items.length === 0) { setSizing(false); return undefined; }
    let cancelled = false;
    setSizing(true);
    setSizeError(null);
    bulkSize(items)
      .then((res) => { if (!cancelled) setSize(res); })
      .catch((err) => {
        if (cancelled) return;
        // Not fatal: the server enforces the cap anyway, so let the user try.
        setSizeError(documentsSearchErrorMessage(err, 'Could not measure the selection.'));
      })
      .finally(() => { if (!cancelled) setSizing(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, overCap]);

  /** Commit one or more comma-separated addresses from the input. */
  const commitDraft = useCallback((raw: string): boolean => {
    const tokens = raw.split(',').map((t) => t.trim()).filter(Boolean);
    if (tokens.length === 0) { setRecipientError(null); return true; }

    const invalid = tokens.find((t) => !isValidEmail(t));
    if (invalid) {
      setRecipientError(`“${invalid}” is not a valid email address.`);
      return false;
    }

    // Built from the CURRENT state, not inside a setRecipients updater: React
    // runs updaters at render time, so a flag set in one would still be false
    // when read here and the cap would silently swallow addresses.
    const next = [...recipients];
    const seen = new Set(next.map((r) => r.toLowerCase()));
    let rejectedForCap = false;
    for (const t of tokens) {
      if (seen.has(t.toLowerCase())) continue;
      if (next.length >= MAX_RECIPIENTS) { rejectedForCap = true; break; }
      next.push(t);
      seen.add(t.toLowerCase());
    }
    setRecipients(next);
    setRecipientError(
      rejectedForCap ? `You can send to at most ${MAX_RECIPIENTS} recipients.` : null,
    );
    return !rejectedForCap;
  }, [recipients]);

  const handleDraftChange = useCallback((value: string) => {
    // A pasted or typed comma is a separator, so commit what precedes it and
    // keep only the tail in the box.
    if (value.includes(',')) {
      const cut = value.lastIndexOf(',');
      if (commitDraft(value.slice(0, cut))) setDraft(value.slice(cut + 1).trimStart());
      else setDraft(value);
      return;
    }
    setDraft(value);
    if (recipientError) setRecipientError(null);
  }, [commitDraft, recipientError]);

  const handleDraftKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (commitDraft(draft)) setDraft('');
    } else if (e.key === 'Backspace' && draft === '') {
      setRecipients((prev) => prev.slice(0, -1));
    }
  }, [commitDraft, draft]);

  const removeRecipient = useCallback((email: string) => {
    setRecipients((prev) => prev.filter((r) => r !== email));
    setRecipientError(null);
  }, []);

  const overLimit = size !== null && size.total_bytes > size.limit_bytes;
  const unknownCount = size ? size.items.filter((i) => i.size_bytes === -1).length : 0;

  const canSend =
    !overCap && !sizing && !overLimit && !sending && result === null
    && recipients.length > 0 && items.length > 0;

  const handleSend = useCallback(async () => {
    setSending(true);
    setSendError(null);
    try {
      const res = await bulkEmail(items, recipients, note.trim() || undefined);
      setResult(res);
      onSent?.(res);
    } catch (err) {
      setSendError(documentsSearchErrorMessage(err, 'Sending failed.'));
    } finally {
      setSending(false);
    }
  }, [items, recipients, note, onSent]);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/50">
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Email documents"
        className="mx-4 flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg bg-panel shadow-xl"
      >
        <ModalHeader
          title={`Email ${items.length.toLocaleString()} document${items.length === 1 ? '' : 's'}`}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {overCap && (
            <p role="alert" className="text-sm font-medium text-amber-700 dark:text-amber-300">
              {items.length.toLocaleString()} documents selected — bulk email is limited to{' '}
              {MAX_BULK_SELECTION} documents. Narrow your filters or select fewer.
            </p>
          )}

          {/* Recipients */}
          <div>
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
              To
            </span>
            <ul
              role="list"
              aria-label="Recipients"
              className="mb-1 flex flex-wrap gap-1.5"
            >
              {recipients.map((r) => (
                <li
                  key={r}
                  className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-sm text-blue-900 dark:bg-blue-900/40 dark:text-blue-100"
                >
                  <span>{r}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${r}`}
                    onClick={() => removeRecipient(r)}
                    className="rounded-full px-1 leading-none text-blue-700 hover:bg-blue-200 hover:text-blue-900 dark:text-blue-200 dark:hover:bg-blue-800"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
            <input
              type="text"
              aria-label="Add recipient"
              placeholder="name@example.com"
              value={draft}
              onChange={(e) => handleDraftChange(e.target.value)}
              onKeyDown={handleDraftKeyDown}
              onBlur={() => { if (commitDraft(draft)) setDraft(''); }}
              className={FIELD}
            />
            {recipientError ? (
              <p role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">
                {recipientError}
              </p>
            ) : (
              <p className="mt-1 text-xs text-muted">
                Press Enter or comma to add. Up to {MAX_RECIPIENTS} recipients.
              </p>
            )}
          </div>

          {/* Note */}
          <div>
            <textarea
              aria-label="Note (optional)"
              placeholder="Add a note to the email (optional)"
              rows={3}
              maxLength={MAX_NOTE}
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE))}
              className={FIELD}
            />
            <p className="mt-1 text-right text-xs text-muted">
              {note.length} / {MAX_NOTE}
            </p>
          </div>

          {/* Size */}
          {!overCap && (
            <div className="rounded-md border border-gray-200 px-3 py-2 text-sm dark:border-gray-700">
              {sizing && (
                <p className="text-gray-600 dark:text-gray-300">Checking size…</p>
              )}
              {!sizing && size && (
                <p className={overLimit ? 'font-medium text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-200'}>
                  Selection: {formatMb(size.total_bytes)} of {formatMb(size.limit_bytes)}
                  {overLimit ? ' — too large to email. Close this and use Download ZIP instead.' : ''}
                </p>
              )}
              {!sizing && sizeError && (
                <p className="text-amber-700 dark:text-amber-300">{sizeError}</p>
              )}
              {!sizing && unknownCount > 0 && (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                  Size unknown for {unknownCount} document{unknownCount === 1 ? '' : 's'} — they
                  are not counted above but will still be attached.
                </p>
              )}
            </div>
          )}

          {/* Outcome */}
          {sendError && (
            <p role="alert" className="text-sm font-medium text-red-600 dark:text-red-400">
              {sendError}
            </p>
          )}
          {result && (
            <div className="rounded-md border border-green-300 bg-green-50 px-3 py-2 dark:border-green-700/50 dark:bg-green-900/20">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                {result.sent} document{result.sent === 1 ? '' : 's'} emailed to{' '}
                {result.recipients.join(', ')}.
              </p>
              {result.failed.length > 0 && (
                <>
                  <p className="mt-2 text-sm font-medium text-amber-800 dark:text-amber-200">
                    {result.failed.length} could not be sent:
                  </p>
                  <ul
                    role="list"
                    aria-label="Documents that failed to send"
                    className="mt-1 list-disc pl-5 text-sm text-amber-800 dark:text-amber-200"
                  >
                    {result.failed.map((f) => (
                      <li key={`${f.pronumber}-${f.filename}`}>
                        {f.filename} (PRO {f.pronumber}) — {f.reason}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 px-6 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {result ? 'Done' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            aria-busy={sending}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BulkEmailModal;
